import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-12120">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-12121">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-12122">
              <span className="text-xs font-bold text-indigo-800" data-id="element-12123">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-12124">
            <div className="text-center" data-id="element-12125">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-12126">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-12127">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-12128">→</div>
            <div className="text-center" data-id="element-12129">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-12130">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-12131">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-12132">
            <table className="w-full text-sm" data-id="element-12133">
              <thead data-id="element-12134">
                <tr className="bg-indigo-100/70" data-id="element-12135">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-12136">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-12137">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-12138">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-12139">
                      <td className="px-3 py-1.5" data-id="element-12140">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-12141">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-12142">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-12143">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-12144">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-12145">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-12146">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-12147">
            <span className="text-2xl" data-id="element-12148">📝</span>
            <div className="text-left" data-id="element-12149">
              <div className="text-sm font-bold text-indigo-900" data-id="element-12150">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-12151">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-12152">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-12153">
            <div className="text-center" data-id="element-12154">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-12155">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-12156">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-12157">→</div>
            <div className="text-center" data-id="element-12158">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-12159">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-12160">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-12161">
          <table className="w-full" data-id="element-12162">
            <thead data-id="element-12163">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-12164">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-12165">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-12166">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-12167">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-12168">
                    <td className="px-4 py-3" data-id="element-12169">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-12170">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-12171">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-12172">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-12173">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-12174">
            <div className="flex items-start gap-3" data-id="element-12175">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-12176">
                <span className="text-blue-600 text-lg" data-id="element-12177">🔍</span>
              </div>
              <div data-id="element-12178">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-12179">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-12180">
                  De <strong data-id="element-12181">stam</strong> krijg je door <strong data-id="element-12182">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-12183">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-12184">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-12185">
              <div className="flex items-start gap-3" data-id="element-12186">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-12187">
                  <span className="text-emerald-600 text-lg" data-id="element-12188">📋</span>
                </div>
                <div data-id="element-12189">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-12190">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-12191">
                    <p data-id="element-12192">
                      <strong data-id="element-12193">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-12194">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-12195">
                      <strong data-id="element-12196">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-12197">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-12198">
                      <strong data-id="element-12199">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-12200">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-12201">
                      <strong data-id="element-12202">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-12203">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'past' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-12204">
              <div className="flex items-start gap-3" data-id="element-12205">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-12206">
                  <span className="text-emerald-600 text-lg" data-id="element-12207">📋</span>
                </div>
                <div data-id="element-12208">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-12209">
                    Patroon Verleden Tijd (Zwakke werkwoorden)
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-12210">
                    <p data-id="element-12211">
                      <strong data-id="element-12212">enkelvoud:</strong> stam + te / de
                    </p>
                    <p data-id="element-12213">
                      <strong data-id="element-12214">meervoud:</strong> stam + ten / den
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-12215">
                      <em data-id="element-12216">
                        Tip: Gebruik 't kofschip om te bepalen of het -te of -de
                        is!
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'perfect' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-12217">
              <div className="flex items-start gap-3" data-id="element-12218">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-12219">
                  <span className="text-emerald-600 text-lg" data-id="element-12220">📋</span>
                </div>
                <div data-id="element-12221">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-12222">
                    Patroon Voltooid Deelwoord
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-12223">
                    <p data-id="element-12224">
                      <strong data-id="element-12225">Zwakke werkwoorden:</strong> ge + stam + t / d
                    </p>
                    <p data-id="element-12226">
                      <strong data-id="element-12227">Sterke werkwoorden:</strong> ge + ... + en
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-12228">
                      <em data-id="element-12229">
                        Tip: Maak het woord langer om te horen of het op een -t
                        of -d eindigt (bijv. gemaakte → gemaakt).
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-12230">
            <div className="flex items-start gap-3" data-id="element-12231">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-12232">
                <span className="text-amber-600 text-lg" data-id="element-12233">💡</span>
              </div>
              <div data-id="element-12234">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-12235">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-12236">
                  Bij <strong data-id="element-12237">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-12238">jij loopt</em> wordt{' '}
                  <em data-id="element-12239">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}